<template>
  <v-dialog
    v-model="dialog"
    content-class="latest-communications-dialog"
    max-width="640px"
    scrollable
  >
    <v-card class="communications-card">
      <v-card-title class="communications-card-title">
        <v-row no-gutters align="center">
          <v-col cols="11" class="d-flex align-center">
            <v-icon class="title-icon" color="primary">{{
              icons.mdiEmailNewsletter
            }}</v-icon>
            <h2 class="text-h5 ml-3 mb-0">Latest Communications</h2>
          </v-col>
          <v-col cols="1" class="text-right">
            <v-icon
              class="modal-close"
              color="black"
              @click.stop="closeDialog()"
              >{{ icons.mdiClose }}</v-icon
            >
          </v-col>
        </v-row>
      </v-card-title>

      <v-divider />

      <v-card-text class="communications-card-text">
        <a
          v-for="item in communications"
          :key="item.id"
          :href="item.url"
          target="_blank"
          rel="noopener noreferrer"
          class="communication-item"
          :class="{ 'communication-unread': !item.read }"
          @click="markAsRead(item)"
        >
          <div class="communication-accent" />
          <div class="communication-body">
            <div class="communication-date">
              <span v-if="!item.read" class="unread-dot" />
              {{ item.date }}
            </div>
            <h3 class="communication-title">{{ item.title }}</h3>
            <p class="communication-preview">{{ item.previewText }}</p>
            <div class="communication-cta">
              Read more
              <v-icon small class="ml-1">{{ icons.mdiOpenInNew }}</v-icon>
            </div>
          </div>
        </a>

        <div v-if="!communications.length" class="communications-empty">
          No communications yet.
        </div>
      </v-card-text>
    </v-card>
  </v-dialog>
</template>

<script>
  import { mdiClose, mdiEmailNewsletter, mdiOpenInNew } from '@mdi/js';
  import {
    ADVISER_COMMUNICATIONS_QUERY,
    MARK_ADVISER_COMMUNICATION_READ_MUTATION,
  } from '../../constants/graphql/advisors';

  export default {
    name: 'LatestCommunicationsDialog',
    props: {
      value: {
        type: Boolean,
        required: true,
      },
    },
    data() {
      return {
        icons: {
          mdiClose,
          mdiEmailNewsletter,
          mdiOpenInNew,
        },
        communications: [],
      };
    },
    apollo: {
      communications: {
        query: ADVISER_COMMUNICATIONS_QUERY,
        update: data => data.adviserCommunications || [],
        fetchPolicy: 'cache-and-network',
        nextFetchPolicy: 'cache-first',
        error(error) {
          console.error('adviserCommunications query error:', error);
        },
      },
    },
    computed: {
      dialog: {
        get() {
          return this.value;
        },
        set(value) {
          this.$emit('input', value);
        },
      },
    },
    methods: {
      closeDialog() {
        this.dialog = false;
      },
      async markAsRead(item) {
        if (item.read) return;
        try {
          await this.$apollo.mutate({
            mutation: MARK_ADVISER_COMMUNICATION_READ_MUTATION,
            variables: { adviserCommunicationId: item.id },
          });
          this.$emit('communication-read');
        } catch (error) {
          console.error('markAdviserCommunicationRead mutation error:', error);
        }
      },
    },
  };
</script>

<style lang="scss" scoped>
  .communications-card {
    border-radius: 12px;
  }

  .communications-card-title {
    padding: 20px 24px;

    .title-icon {
      font-size: 28px;
    }

    h2 {
      font-weight: 600;
    }
  }

  .modal-close {
    cursor: pointer;
  }

  .communications-card-text {
    padding-top: 30px !important;
    padding-bottom: 30px !important;
    padding-left: 24px !important;
    padding-right: 24px !important;
  }

  .communication-item {
    display: flex;
    text-decoration: none;
    color: inherit;
    border: 1px solid rgba(0, 0, 0, 0.08);
    border-radius: 10px;
    overflow: hidden;
    margin-bottom: 16px;
    background-color: #fff;
    transition: box-shadow 0.2s ease, transform 0.2s ease,
      border-color 0.2s ease;

    &:last-child {
      margin-bottom: 0;
    }

    &:hover {
      box-shadow: 0 6px 18px rgba(0, 0, 0, 0.1);
      transform: translateY(-2px);
      border-color: rgba(0, 0, 0, 0.12);

      .communication-cta {
        opacity: 1;
      }
    }
  }

  .communication-accent {
    flex: 0 0 6px;
    background: #d0d0d0;
    transition: background 0.3s ease;
  }

  .communication-unread .communication-accent {
    background: linear-gradient(180deg, #5db075 0%, #3b8c57 100%);
  }

  .communication-body {
    padding: 16px 20px;
    flex: 1 1 auto;
  }

  .communication-date {
    font-size: 12px;
    font-weight: 600;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: #8a8a8a;
    margin-bottom: 6px;
  }

  .communication-unread .communication-title {
    font-weight: 700;
  }

  .unread-dot {
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #3b8c57;
    margin-right: 6px;
  }

  .communication-title {
    font-size: 17px;
    font-weight: 600;
    line-height: 1.3;
    color: #1f1f1f;
    margin-bottom: 8px;
  }

  .communication-preview {
    font-size: 14px;
    line-height: 1.5;
    color: #5a5a5a;
    margin-bottom: 12px;
  }

  .communication-cta {
    display: inline-flex;
    align-items: center;
    font-size: 13px;
    font-weight: 600;
    color: #3b8c57;
    opacity: 0.85;
    transition: opacity 0.2s ease;
  }

  .communications-empty {
    text-align: center;
    color: #8a8a8a;
    padding: 32px 0;
    font-size: 14px;
  }
</style>
